// @flow

import * as R from 'ramda';

import { AceService, ApolloService, HttpService } from '~/services';
import {
  getRouteNavigationParamsWithFormattedDate,
  renameKeys,
  isValidNumber,
} from '~/helpers';
import { ELEVATE_TOKENS, ELEVATE_TOKENS_MUTATION } from '~/gql';

const getAmountValue = (v: string | number): number => {
  let value = +v;

  return isValidNumber(value) ? value : 0;
};

export const resolvers = {
  Query: {
    withdrawalList: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      props: RouteNavigationInputProps,
      { cache }: ApolloCache,
    ): Promise<?WithdrawalListResponse> => {
      const columnsMapping = {
        DateTimeUtc: 'date',
        TrxId: 'trxId',
        TrxFee: 'trxFee',
        Amount: 'amount',
        NetAmount: 'netAmount',
      };

      const params = getRouteNavigationParamsWithFormattedDate(
        {
          date: ['DateFromUtc', 'DateToUtc'],
          trxId: 'TrxId',
          amount: ['AmountFrom', 'AmountTo'],
          trxFee: ['TrxFeeFrom', 'TrxFeeTo'],
          netAmount: ['NetAmountFrom', 'NetAmountTo'],
        },
        R.invertObj(columnsMapping),
      )(props);

      // Get list items
      const {
        data: { WithdrawalTransactionList, PageCount = 1 },
      } = await HttpService.client.get(
        '/MyWealth/Withdrawal/GetWithdrawalTrxList',
        { params },
      );

      const items: WithdrawalListItem[] = R.map(
        R.pipe(
          renameKeys(columnsMapping),
          R.evolve({
            amount: parseFloat,
            netAmount: parseFloat,
            trxFee: parseFloat,
          }),
          R.assoc('__typename', 'WithdrawalListItem'),
        ),
        WithdrawalTransactionList,
      );

      const withdrawalList = {
        data: items,
        pages: PageCount,
        __typename: 'WithdrawalList',
      };

      cache.writeData({ data: { withdrawalList }, variables: props });

      return withdrawalList;
    },
    withdrawalTransactionFee: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      props: WithdrawalTransactionFeeQueryInput,
      { cache }: ApolloCache,
    ): Promise<WithdrawalTransactionFee> => {
      const params = renameKeys({
        amount: 'Amount',
      })(props);

      // Get transaction fee
      const {
        data: { TrxFee },
      } = await HttpService.client.get('/MyWealth/Withdrawal/GetTrxFee', {
        params,
      });

      const value = getAmountValue(R.prop('amount', props));
      const amountBTC = R.divide(value, 1000);
      const trxFee = parseFloat(TrxFee);
      const trxFeeBTC = R.divide(trxFee, 1000);

      const withdrawalTransactionFee: WithdrawalTransactionFee = {
        amount: value,
        amountBTC,
        trxFee,
        trxFeeBTC,
        __typename: 'WithdrawalTransactionFee',
      };

      cache.writeData({
        data: { withdrawalTransactionFee },
        variables: props,
      });

      return withdrawalTransactionFee;
    },
  },
  Mutation: {
    createWithdrawalTransaction: async (
      _, // eslint-disable-line no-unused-vars
      { values }: WithdrawalTransactionInputMutation,
    ): Promise<MessagePayload> => {
      const params = renameKeys({
        amount: 'Amount',
        IdToken: 'idToken',
        trxFee: 'TrxFee',
        withdrawalBTCAddress: 'BtcAddress',
      })(
        R.pick(['amount', 'idToken', 'trxFee', 'withdrawalBTCAddress'], values),
      );
      const authTokens = await AceService.getAuthTokens();

      params.NetAmount = +(params.Amount - params.TrxFee).toFixed(8);
      HttpService.setAuthorizationToken(values.accessToken);

      try {
        await HttpService.client.post(
          '/MyWealth/Withdrawal/WithdrawApplication',
          params,
        );
        await ApolloService.client.mutate({
          mutation: ELEVATE_TOKENS_MUTATION,
          refetchQueries: [
            {
              query: ELEVATE_TOKENS,
            },
          ],
          variables: {
            values: null,
          },
        });
        HttpService.setAuthorizationToken(authTokens.accessToken);

        return {
          code: 0,
          message: 'Done',
          __typename: 'MessagePayload',
        };
      } catch (error) {
        HttpService.setAuthorizationToken(authTokens.accessToken);

        return {
          code: 0,
          message: error.message,
          __typename: 'MessagePayload',
        };
      }
    },
  },
};
